import React from 'react';
import { CheckCircle, AlertCircle, AlertTriangle, XCircle } from 'lucide-react';
import type { ComplianceAnalysis } from '../../../types/kyb.types';

interface StatusBannerProps {
  complianceAnalysis?: ComplianceAnalysis;
}

export const StatusBanner: React.FC<StatusBannerProps> = ({ complianceAnalysis }) => {
  if (!complianceAnalysis?.banner_config) {
    return null;
  }
  
  const { banner_config } = complianceAnalysis;
  
  // Don't show banner if it's empty or missing required fields
  if (!banner_config.title || !banner_config.message) {
    return null;
  }
  
  // Updated color schemes with proper contrast for text visibility
  const colorSchemes: Record<string, { bg: string; text: string; iconBg: string }> = {
    green: { 
      bg: "from-green-500 to-green-600", 
      text: "text-white",
      iconBg: "bg-white/20"
    },
    yellow: { 
      bg: "from-yellow-400 to-yellow-500", 
      text: "text-gray-900",  // Dark text for yellow background
      iconBg: "bg-gray-900/10"
    },
    orange: { 
      bg: "from-orange-500 to-red-500", 
      text: "text-white",
      iconBg: "bg-white/20"
    },
    red: { 
      bg: "from-red-500 to-red-600", 
      text: "text-white",
      iconBg: "bg-white/20"
    },
    gray: { 
      bg: "from-gray-500 to-gray-600", 
      text: "text-white",
      iconBg: "bg-white/20"
    },
    white: { 
      bg: "from-gray-100 to-gray-200",  // Light gray instead of pure white
      text: "text-gray-900",  // Dark text for light background
      iconBg: "bg-gray-900/10"
    }
  };

  const scheme = colorSchemes[banner_config.color_scheme] || colorSchemes.gray;

  const icons: Record<string, React.ReactNode> = {
    check_circle: <CheckCircle className={`w-7 h-7 ${scheme.text}`} />,
    warning: <AlertCircle className={`w-7 h-7 ${scheme.text}`} />,
    alert: <AlertTriangle className={`w-7 h-7 ${scheme.text}`} />,
    critical: <XCircle className={`w-7 h-7 ${scheme.text}`} />
  };

  return (
    <div className={`bg-gradient-to-r ${scheme.bg} rounded-xl p-5 shadow-lg`}>
      <div className="flex items-start space-x-4">
        <div className={`w-12 h-12 ${scheme.iconBg} rounded-full flex items-center justify-center`}>
          {icons[banner_config.icon] || icons.warning}
        </div>
        <div className="flex-1">
          <h3 className={`${scheme.text} font-bold text-xl mb-1`}>
            {banner_config.title}
          </h3>
          <p className={`${scheme.text} opacity-90 text-sm`}>
            {banner_config.message}
          </p>
        </div>
      </div>
    </div>
  );
};

export default StatusBanner;
